import { useEffect, useState } from "react";
import { marketplaceApi, phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";

const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || "http://127.0.0.1:8000";

const stockOptions = [
  { value: "AVAILABLE", label: "Available" },
  { value: "IN_STOCK", label: "In stock" },
  { value: "OUT_OF_STOCK", label: "Out of stock" },
  { value: "UNAVAILABLE", label: "Unavailable" },
  { value: "DEFECTIVE_BATCH", label: "Defective batch" },
];

function normaliseMedia(src) {
  if (!src) return "";
  if (src.startsWith("http://") || src.startsWith("https://") || src.startsWith("/images/")) return src;
  if (src.startsWith("/media/")) return `${API_ORIGIN}${src}`;
  if (src.startsWith("media/")) return `${API_ORIGIN}/${src}`;
  return src;
}

function emptyProductForm() {
  return {
    id: null,
    title: "",
    brand: "",
    model_number: "",
    category: "",
    price_min: "",
    price_max: "",
    moq_text: "",
    unit_of_measure: "",
    available_quantity: "",
    stock_status: "AVAILABLE",
    supplier_location: "",
    short_description: "",
    image: null,
    existing_image: "",
  };
}

function Modal({ title, eyebrow, children, onClose }) {
  return (
    <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
      <section className="tazy-modal-card product-modal-card product-editor-modal">
        <header className="tazy-modal-header">
          <div>
            <span>{eyebrow}</span>
            <h3>{title}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close modal">×</button>
        </header>
        <div className="tazy-modal-body">{children}</div>
      </section>
    </div>
  );
}

function statusClass(status) {
  const value = String(status || "").toUpperCase();
  if (["PUBLISHED", "AVAILABLE", "IN_STOCK"].includes(value)) return "success";
  if (["REJECTED", "ARCHIVED", "OUT_OF_STOCK", "UNAVAILABLE", "DEFECTIVE_BATCH"].includes(value)) return "danger";
  return "warning";
}

export default function ProductCatalogue() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showProductModal, setShowProductModal] = useState(false);
  const [savingStock, setSavingStock] = useState("");
  const [form, setForm] = useState(emptyProductForm());

  const isAdmin = user?.is_platform_staff || ["SUPER_ADMIN", "ADMIN", "SUPPORT", "FINANCE", "OPERATIONS"].includes(user?.account_type);
  const isBusiness = user?.account_type === "BUSINESS";
  const isSeller = ["SELLER", "BUSINESS"].includes(user?.account_type);

  async function loadData() {
    setLoading(true);
    setError("");
    try {
      const [productRes, categoryRes] = await Promise.all([phase1Api.products(), marketplaceApi.categories()]);
      setProducts(Array.isArray(productRes.data) ? productRes.data : []);
      setCategories(Array.isArray(categoryRes.data) ? categoryRes.data : []);
    } catch (err) {
      setError("Could not load catalogue data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  function update(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function openAddProduct() {
    setForm(emptyProductForm());
    setShowProductModal(true);
  }

  function openEditProduct(product) {
    setForm({
      id: product.id,
      title: product.title || "",
      brand: product.brand || "",
      model_number: product.model_number || "",
      category: product.category || "",
      price_min: product.price_min ?? "",
      price_max: product.price_max ?? "",
      moq_text: product.moq_text || "",
      unit_of_measure: product.unit_of_measure || "",
      available_quantity: product.available_quantity ?? "",
      stock_status: product.stock_status || "AVAILABLE",
      supplier_location: product.supplier_location || "",
      short_description: product.short_description || "",
      image: null,
      existing_image: normaliseMedia(product.image_src || ""),
    });
    setShowProductModal(true);
  }

  async function submitProduct(event) {
    event.preventDefault();
    setMessage("");
    setError("");
    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (["id", "existing_image"].includes(key)) return;
      if (value !== "" && value !== null && value !== undefined) payload.append(key, value);
    });
    try {
      const res = form.id ? await phase1Api.updateProduct(form.id, payload) : await phase1Api.createProduct(payload);
      const status = res.data?.status;
      setMessage(form.id ? "Product updated successfully." : (status === "PUBLISHED" ? "Product added and published." : `Product saved with status: ${status}.`));
      setForm(emptyProductForm());
      setShowProductModal(false);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || "Product could not be saved.");
    }
  }

  async function decide(productId, action) {
    setMessage("");
    setError("");
    try {
      await phase1Api.productDecision(productId, { action });
      setMessage(`Product action completed: ${action}`);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || "Product action failed.");
    }
  }

  async function updateStock(productId, payload) {
    setSavingStock(String(productId));
    setMessage("");
    setError("");
    try {
      await phase1Api.productStock(productId, payload);
      setMessage("Stock status updated.");
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.detail || "Stock update failed.");
    } finally {
      setSavingStock("");
    }
  }

  const title = isAdmin ? "Marketplace Catalogue" : isBusiness ? "Business Product Catalogue" : "Product Catalogue";
  const subtitle = isAdmin
    ? "Platform-wide catalogue moderation, visibility and flagged listing control. Sellers and business accounts manage their own product records from their workspace."
    : isBusiness
      ? "Manage product listings, stock status and availability for the business account. Inventory movement tools will build on this catalogue foundation."
      : "Manage products, images, pricing and availability. My Store remains focused on profile, categories and verification.";

  return (
    <div className={`portal-shell-v2 ${collapsed ? "sidebar-collapsed" : ""}`}>
      <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      <main className="portal-main-v2 workspace-main catalogue-main compact-workspace-main">
        <PortalTopbar user={user} onLogout={logout} />
        <section className="workspace-hero catalogue-hero compact-workspace-hero catalogue-hero-clean">
          <div><span>{isAdmin ? "PRODUCT CONTROL" : isBusiness ? "BUSINESS CATALOGUE" : "SELLER CATALOGUE"}</span><h1>{title}</h1><p>{subtitle}</p></div>
          <div className="catalogue-hero-actions">
            <div className="verification-meter compact-meter"><strong>{products.length}</strong><span>Products</span></div>
            {isSeller && <button type="button" className="primary-action-btn" onClick={openAddProduct}>Add product</button>}
          </div>
        </section>
        {message && <div className="success-box workspace-alert">{message}</div>}
        {error && <div className="error-box workspace-alert">{error}</div>}

        <section className="workspace-card catalogue-list-card refined-catalogue-card professional-products-card">
          <div className="workspace-section-title"><span>{isAdmin ? "PUBLIC VISIBILITY" : "MY PRODUCTS"}</span><h2>{isAdmin ? "Moderation and availability overview" : "Catalogue manager"}</h2></div>
          {loading ? <p>Loading products...</p> : (
            <div className="catalogue-table refined-catalogue-table professional-product-list">
              {products.length === 0 ? <p className="soft-text">No products yet. Use Add product to start building the catalogue.</p> : products.map((product) => (
                <article key={product.id} className="catalogue-row refined-catalogue-row professional-product-row">
                  <div className="catalogue-image">{product.image_src ? <img src={normaliseMedia(product.image_src)} alt={product.title} /> : <span>No image</span>}</div>
                  <div className="catalogue-info"><strong>{product.title}</strong><span>{product.category_name || "No category"} • {product.seller_display || product.seller_name || "Seller"}</span><p>{product.price_range} {product.available_quantity !== null && product.available_quantity !== undefined ? `• Internal stock: ${product.available_quantity} ${product.unit_of_measure || "ITEM"}` : ""}</p></div>
                  <div className="catalogue-status-stack">
                    <span className={`status-pill ${statusClass(product.status)}`}>{product.status}</span>
                    <span className={`status-pill ${statusClass(product.stock_status)}`}>{String(product.stock_status || "AVAILABLE").replaceAll("_", " ")}</span>
                  </div>
                  {(isSeller || isAdmin) && (
                    <div className="stock-control-mini">
                      <select value={product.stock_status || "AVAILABLE"} disabled={savingStock === String(product.id)} onChange={(e) => updateStock(product.id, { stock_status: e.target.value, available_quantity: product.available_quantity ?? "" })}>
                        {stockOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </select>
                      <input type="number" min="0" defaultValue={product.available_quantity ?? ""} placeholder="Qty" onBlur={(e) => updateStock(product.id, { stock_status: product.stock_status || "AVAILABLE", available_quantity: e.target.value })} />
                    </div>
                  )}
                  <div className="catalogue-actions catalogue-actions--product">
                    {(isSeller || isAdmin) && <button title="Edit product" className="edit-action" onClick={() => openEditProduct(product)}>✎</button>}
                    {isAdmin && <><button title="Publish" onClick={() => decide(product.id, "PUBLISH")}>✓</button><button title="Review" onClick={() => decide(product.id, "REVIEW")}>!</button><button title="Feature" onClick={() => decide(product.id, "FEATURE")}>★</button><button title="Reject" onClick={() => decide(product.id, "REJECT")}>×</button></>}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {showProductModal && (
        <Modal title={form.id ? "Edit catalogue product" : "Add catalogue product"} eyebrow="PRODUCT ENTRY" onClose={() => setShowProductModal(false)}>
          <form className="product-form-grid modal-form-grid" onSubmit={submitProduct}>
            {form.existing_image && <div className="full existing-product-preview"><span>Current image</span><img src={form.existing_image} alt="Current product" /></div>}
            <div><label>Product name</label><input value={form.title} onChange={(e) => update("title", e.target.value)} required /></div>
            <div><label>Category</label><select value={form.category} onChange={(e) => update("category", e.target.value)} required><option value="">Select category</option>{categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}</select></div>
            <div><label>Brand</label><input value={form.brand} onChange={(e) => update("brand", e.target.value)} /></div>
            <div><label>Model / SKU</label><input value={form.model_number} onChange={(e) => update("model_number", e.target.value)} /></div>
            <div><label>Price from</label><input type="number" step="0.01" value={form.price_min} onChange={(e) => update("price_min", e.target.value)} /></div>
            <div><label>Price to</label><input type="number" step="0.01" value={form.price_max} onChange={(e) => update("price_max", e.target.value)} /></div>
            <div><label>Internal stock quantity</label><input type="number" min="0" value={form.available_quantity} onChange={(e) => update("available_quantity", e.target.value)} /></div>
            <div><label>Stock status</label><select value={form.stock_status} onChange={(e) => update("stock_status", e.target.value)}>{stockOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></div>
            <div><label>Unit of measure</label><input value={form.unit_of_measure} onChange={(e) => update("unit_of_measure", e.target.value)} placeholder="Each, kg, carton..." /></div>
            <div><label>MOQ</label><input value={form.moq_text} onChange={(e) => update("moq_text", e.target.value)} placeholder="Example: 1 carton" /></div>
            <div className="full"><label>Supplier location</label><input value={form.supplier_location} onChange={(e) => update("supplier_location", e.target.value)} /></div>
            <div className="full"><label>Description</label><textarea rows="4" value={form.short_description} onChange={(e) => update("short_description", e.target.value)} /></div>
            <div className="full"><label>Product image</label><input type="file" accept="image/*" onChange={(e) => update("image", e.target.files?.[0] || null)} /></div>
            <button type="submit">{form.id ? "Update product" : "Save product"}</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
